"use client";

import React, { useState } from "react";
import { CircleAlert as AlertCircle, CircleCheck as CheckCircle2, Mail, Phone, Send, Sparkles } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
const LEAD_WEBHOOK_URL = "https://hooks.zapier.com/hooks/catch/28361868/4mesgtq/";
const PROJECT_TYPE_OPTIONS = ["Kitchen", "Bathroom", "Whole-Home", "Home Addition", "Basement", "Interior", "Outdoor Living", "Deck", "Other"];
const TIMING_OPTIONS = ["As soon as possible", "1-3 months", "3-6 months", "6+ months / just exploring"];
const BUDGET_OPTIONS = ["Under $25,000", "$25,000-$75,000", "$75,000-$150,000", "$150,000-$300,000", "$300,000+", "Prefer not to say"];
export default function EstimateRequestForm() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [zip, setZip] = useState("");
  const [projectType, setProjectType] = useState("Bathroom");
  const [timing, setTiming] = useState("");
  const [budget, setBudget] = useState("");
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);
  const [zipError, setZipError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const validateZip = (value: string) => {
    return /^\d{5}$/.test(value.trim());
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(false);
    if (!validateZip(zip)) {
      setZipError("Please enter a valid 5-digit ZIP code.");
      return;
    }
    setZipError("");
    if (!consent) {
      return;
    }
    setSubmitting(true);
    try {
      const payload = new URLSearchParams({
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        project_type: projectType,
        address: zip.trim(),
        zip: zip.trim(),
        details: message.trim(),
        timing: timing,
        budget: budget,
        contact_method: "Phone",
        source: "Brookhaven Elevation website",
        page: window.location.href,
        form_location: "bathroom-remodeling-page"
      });
      const response = await fetch(LEAD_WEBHOOK_URL, {
        method: "POST",
        body: payload
      });
      if (response.ok) {
        setSubmitSuccess(true);
      } else {
        setSubmitError(true);
      }
    } catch {
      setSubmitError(true);
    } finally {
      setSubmitting(false);
    }
  };
  return <section id="estimaterequestform" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="relative mx-auto max-w-4xl">
        <div className="rounded-2xl border border-border bg-card/85 backdrop-blur-md p-6 sm:p-8 lg:p-10 shadow-xl transition-all">
          {submitSuccess ? <div className="py-12 px-4 text-center space-y-6">
              <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-primary/20 text-primary">
                <CheckCircle2 className="size-8" />
              </div>
              <div className="space-y-2">
                <h3 className="text-2xl sm:text-3xl font-serif font-bold text-foreground">
                  Estimate Request Received
                </h3>
                <p className="text-muted-foreground max-w-md mx-auto text-base">
                  Thank you, {name || "there"}. A residential specialist from Brookhaven Elevation Renovations will review your specifications and contact you promptly.
                </p>
              </div>
              <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-muted-foreground">
                <a href="tel:(470)847-9076" className="inline-flex items-center gap-2 hover:text-foreground font-medium transition-colors">
                  <Phone className="size-4 text-primary" />
                  (470) 847-9076
                </a>
                <span className="hidden sm:inline" aria-hidden="true">•</span>
                <a href="mailto:serviceleadalerts@gmail.com" className="inline-flex items-center gap-2 hover:text-foreground font-medium transition-colors">
                  <Mail className="size-4 text-primary" />
                  serviceleadalerts@gmail.com
                </a>
              </div>
            </div> : <div>
              <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-10 space-y-3">
                <Badge variant="outline" className="border-primary/40 bg-accent text-accent-foreground px-3 py-1 text-xs uppercase tracking-wide">
                  <Sparkles className="size-3 text-primary" />
                  Request An Estimate
                </Badge>
                <h2 className="font-serif text-3xl sm:text-4xl font-bold tracking-tight text-foreground">
                  Plan Your Bathroom Renovation
                </h2>
                <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                  Provide your project details below and we will follow up to discuss your project with a personalized scope and estimated investment timeline.
                </p>
              </div>

              {submitError && <div className="mb-8">
                  <Alert variant="destructive">
                    <AlertCircle className="size-4" />
                    <AlertTitle>Submission Unsuccessful</AlertTitle>
                    <AlertDescription className="text-sm">
                      We were unable to process your request online right now. Please reach us directly by calling{" "}
                      <a href="tel:(470)847-9076" className="font-semibold underline hover:text-foreground">
                        (470) 847-9076
                      </a>{" "}
                      or emailing{" "}
                      <a href="mailto:serviceleadalerts@gmail.com" className="font-semibold underline hover:text-foreground">
                        serviceleadalerts@gmail.com
                      </a>
                      .
                    </AlertDescription>
                  </Alert>
                </div>}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="estimate-name">
                      Full Name <span className="text-destructive">*</span>
                    </Label>
                    <Input id="estimate-name" type="text" placeholder="Jane Doe" required value={name} onChange={e => setName(e.target.value)} className="bg-background/80" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="estimate-phone">
                      Phone Number <span className="text-destructive">*</span>
                    </Label>
                    <Input id="estimate-phone" type="tel" placeholder="(470) 847-9076" required value={phone} onChange={e => setPhone(e.target.value)} className="bg-background/80" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="estimate-email">
                      Email Address <span className="text-destructive">*</span>
                    </Label>
                    <Input id="estimate-email" type="email" placeholder="jane@example.com" required value={email} onChange={e => setEmail(e.target.value)} className="bg-background/80" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="estimate-zip">
                      ZIP Code <span className="text-destructive">*</span>
                    </Label>
                    <Input id="estimate-zip" type="text" placeholder="30319" required maxLength={5} value={zip} onChange={e => {
                  setZip(e.target.value);
                  if (zipError && /^\d{5}$/.test(e.target.value)) {
                    setZipError("");
                  }
                }} className={zipError ? "border-destructive bg-background/80" : "bg-background/80"} />
                    {zipError && <p className="text-destructive text-xs font-medium">
                        {zipError}
                      </p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="estimate-project-type">
                      Project Type <span className="text-destructive">*</span>
                    </Label>
                    <select id="estimate-project-type" value={projectType} onChange={e => setProjectType(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background/80 px-3 py-1 text-base md:text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]">
                      {PROJECT_TYPE_OPTIONS.map((item, index) => <option key={item} value={item} data-index={index}>
                          {item}
                        </option>)}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="estimate-timing">Preferred Timing</Label>
                    <Select value={timing} onValueChange={val => setTiming(val)}>
                      <SelectTrigger id="estimate-timing" className="w-full bg-background/80">
                        <SelectValue placeholder="Select timeframe" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIMING_OPTIONS.map((item, index) => <SelectItem key={item} value={item} data-index={index}>
                            {item}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="estimate-budget">Budget Range</Label>
                    <Select value={budget} onValueChange={val => setBudget(val)}>
                      <SelectTrigger id="estimate-budget" className="w-full bg-background/80">
                        <SelectValue placeholder="Select budget" />
                      </SelectTrigger>
                      <SelectContent>
                        {BUDGET_OPTIONS.map((item, index) => <SelectItem key={item} value={item} data-index={index}>
                            {item}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="estimate-message">
                    Project Details & Scope (Optional)
                  </Label>
                  <Textarea id="estimate-message" placeholder="Tell us about your space, desired layout adjustments, fixture preferences, or architectural details..." value={message} onChange={e => setMessage(e.target.value)} rows={4} className="bg-background/80 min-h-24" />
                </div>

                <div className="pt-2">
                  <div className="flex items-start gap-3">
                    <Checkbox id="estimate-consent" checked={consent} onCheckedChange={checked => setConsent(checked === true)} required className="mt-0.5" />
                    <Label htmlFor="estimate-consent" className="text-xs sm:text-sm font-normal text-muted-foreground leading-snug cursor-pointer select-none">
                      I agree to be contacted about my project by phone, text, or email.
                    </Label>
                  </div>
                </div>

                <div className="pt-4 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-border/60">
                  <p className="text-xs text-muted-foreground order-2 sm:order-1 text-center sm:text-left">
                    Direct inquiries:{" "}
                    <a href="tel:(470)847-9076" className="text-foreground font-medium underline-offset-2 hover:underline">
                      (470) 847-9076
                    </a>
                  </p>
                  <Button type="submit" disabled={submitting || !consent} className="w-full sm:w-auto min-w-44 order-1 sm:order-2">
                    {submitting ? "Submitting..." : <>
                        Request Estimate
                        <Send className="size-4" />
                      </>}
                  </Button>
                </div>
              </form>
            </div>}
        </div>
      </div>
    </section>;
}